import { TextareaAutosize } from '@mui/base';
import cn from 'classnames';
import { forwardRef, useState } from 'react';

import { useFieldId } from '../../../utils';
import { BaseField, BaseInput, FieldLabel } from '../../internal';

import type { ForwardedRef } from 'react';

import type { BaseInputProps } from '../../internal';
import type { TextFieldProps } from './text-field.types';

import './text-field.scss';

/**
 * The text field includes a label, a helper text and a textarea field where users can type multiline of text.
 * The text field component typically appears in forms and dialogs.
 *
 * ## Usage
 *
 * ### Basic
 *
 * ```jsx
 * import { TextField } from '@bloomreach/react-banana-ui';
 *
 * export default function TextFieldExample() {
 *   return <TextField placeholder="Placeholder..." />;
 * }
 * ```
 *
 * ### With label and helper text
 * ```jsx
 * import { TextField } from '@bloomreach/react-banana-ui';
 *
 * export default function TextFieldExample() {
 *   return (
 *     <TextField
 *       label="Field with a label"
 *       helperText="This is helper text"
 *     />
 *   );
 * }
 * ```
 */
const TextField = forwardRef((props: TextFieldProps, ref: ForwardedRef<HTMLTextAreaElement>) => {
  const {
    autoSize,
    className,
    counter,
    defaultValue,
    disabled,
    error,
    helperText,
    id,
    label,
    maxCount,
    maxRows,
    minRows = 3,
    name,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    readOnly,
    required,
    resizable,
    textareaProps,
    tooltip,
    value,
    ...rest
  } = props;

  const controlId = useFieldId(id ?? textareaProps?.id, 'rbui-text-field');

  const baseInputProps: BaseInputProps = {
    defaultValue,
    disabled,
    error,
    id: controlId,
    inputProps: {
      ...textareaProps,
      className: cn('rbui-text-field__textarea', textareaProps?.className, {
        'rbui-text-field__textarea--resizable': resizable,
      }),
    },
    maxRows,
    minRows,
    name,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    readOnly,
    required,
    value,
  };

  const [count, setCount] = useState(0);

  const fieldLabel = label && (
    <FieldLabel htmlFor={controlId} required={required} tooltip={tooltip}>
      {label === ' ' ? <span>&nbsp;</span> : label}
    </FieldLabel>
  );

  return (
    <BaseField
      {...rest}
      className={cn('rbui-text-field', className)}
      control={
        <BaseInput
          {...baseInputProps}
          inputRef={ref}
          onCountChange={setCount}
          renderInput={(inputProps, inputRef) =>
            autoSize ? (
              <TextareaAutosize {...inputProps} maxRows={maxRows} minRows={minRows} ref={inputRef} />
            ) : (
              <textarea {...inputProps} ref={inputRef} rows={minRows} />
            )
          }
        />
      }
      error={error}
      helperText={helperText}
      label={fieldLabel}
      metadata={
        counter && <span className="rbui-text-field__counter">{`${count}${maxCount ? `/${maxCount}` : ''}`}</span>
      }
    />
  );
});

TextField.displayName = 'TextField';

export default TextField;
